import { CoverImageFragment } from "@src/fragments.generated";
import CoverImagePickerEmojiTab from "@src/features/contentDetail/ui/CoverImagePickerEmojiTab";
import CoverImagePickerImagesTab from "@src/features/contentDetail/ui/CoverImagePickerImagesTab";

import { Tabs, TabsList, TabsTrigger } from "@src/lib/ui/Tabs";
import { useS3Upload } from "@src/features/app/hooks/useS3Upload";
import { BackgroundGradient, CoverImageInput } from "@src/types.generated";
import React, { FC, useState, useCallback } from "react";

type Props = {
  coverImage?: CoverImageFragment;
  onCoverImageSelected?: (coverImage: Omit<CoverImageInput, "id">) => void;
  allowedGradients?: BackgroundGradient[];
};

const CoverImagePicker: FC<Props> = React.forwardRef<HTMLDivElement, Props>(
  ({ coverImage, onCoverImageSelected, allowedGradients }, ref) => {
    const [tab, setTab] = useState("Images");
    const [emoji, setEmoji] = useState("");
    const [selectedGradient, setSelectedGradient] =
      useState<BackgroundGradient | null>(null);

    const updateImage = useCallback(
      (url: string) => {
        if (!onCoverImageSelected) return;
        onCoverImageSelected({
          mediaUrl: url,
          emojiIcon: null,
        });
        setEmoji("");
        setSelectedGradient(null);
      },
      [onCoverImageSelected],
    );

    const { handleFileUpload, isUploading: fileUploadInProgress } = useS3Upload(
      {
        onUploadComplete: (upload) => {
          if (!onCoverImageSelected) return;
          onCoverImageSelected({
            mediaUrl: upload.url,
            emojiIcon: null,
          });
          setEmoji("");
          setSelectedGradient(null);
        },
      },
    );

    const updateGradient = useCallback(
      (gradient: BackgroundGradient) => {
        if (!onCoverImageSelected) return;
        setSelectedGradient(gradient);
        onCoverImageSelected({
          emojiIcon: emoji || coverImage?.emojiIcon,
          background: gradient,
          mediaUrl: null,
        });
      },
      [onCoverImageSelected, emoji, coverImage?.emojiIcon],
    );

    const updateEmoji = useCallback(
      (emojiIcon: string) => {
        setEmoji(emojiIcon);
        if (!onCoverImageSelected) return;
        onCoverImageSelected({
          emojiIcon: emojiIcon,
          background: selectedGradient || coverImage?.background,
          mediaUrl: null,
        });
        setSelectedGradient(null);
      },
      [onCoverImageSelected, coverImage?.background, selectedGradient],
    );

    const clearEmoji = useCallback(() => {
      if (!onCoverImageSelected) return;
      onCoverImageSelected({
        emojiIcon: null,
        background: selectedGradient || coverImage?.background,
      });
      setEmoji("");
    }, [onCoverImageSelected, selectedGradient, coverImage?.background]);

    return (
      <div
        ref={ref}
        className="flex h-[425px] flex-col items-center justify-start rounded-2xl p-4"
      >
        <div className="flex self-stretch border-b border-border-primary">
          <Tabs asChild size="sm" value={tab} onValueChange={setTab}>
            <TabsList fill aria-label="Cover type">
              <TabsTrigger value="Images">Images</TabsTrigger>
              <TabsTrigger value="Emoji">Emoji</TabsTrigger>
            </TabsList>
          </Tabs>
        </div>
        {tab === "Images" && (
          <CoverImagePickerImagesTab
            updateImage={updateImage}
            onFileSelected={handleFileUpload}
            fileUploadInProgress={fileUploadInProgress}
          />
        )}
        {tab === "Emoji" && (
          <CoverImagePickerEmojiTab
            coverImage={coverImage}
            selectedGradient={selectedGradient}
            setSelectedGradient={updateGradient}
            emoji={emoji}
            setEmoji={updateEmoji}
            clearEmoji={clearEmoji}
            allowedGradients={allowedGradients}
          />
        )}
      </div>
    );
  },
);

export default CoverImagePicker;
